import { resolveEventAdmissionMode, type EventAdmissionMode } from "../../../convex/lib/eventTicketing";

type AdmissionInput = {
  status: string;
  admissionMode?: EventAdmissionMode | null;
  ticketLink?: string | null;
  slug?: string | null;
};

export function resolvePublicEventAdmission(input: AdmissionInput):
  | { kind: "free_rsvp" }
  | { kind: "external_ticket"; href: string }
  | { kind: "first_party_ticket"; href: string }
  | { kind: "none" } {
  const mode = resolveEventAdmissionMode(input);
  if (mode === "free_rsvp") return { kind: "free_rsvp" };
  if (mode === "external_ticket" && input.ticketLink?.trim()) {
    return { kind: "external_ticket", href: input.ticketLink.trim() };
  }
  if (mode === "first_party_ticket" && input.slug?.trim()) {
    return { kind: "first_party_ticket", href: `/events/${input.slug}/tickets` };
  }
  return { kind: "none" };
}

/** "Sold Out" is reserved for an operator pause, never capacity exhaustion. */
export function eventAdmissionPausedLabel(input: {
  ticketSalesPaused?: boolean | null;
}): { paused: boolean; label: string } {
  if (input.ticketSalesPaused === true) {
    return { paused: true, label: "Sold Out" };
  }
  return { paused: false, label: "" };
}

export function freeRsvpGuestLabel(input: {
  ticketSalesPaused?: boolean | null;
}): { paused: boolean; label: string } {
  const paused = eventAdmissionPausedLabel(input);
  if (paused.paused) return paused;
  return { paused: false, label: "Join the Guest List" };
}

export function firstPartyTicketGuestLabel(input: {
  ticketSalesPaused?: boolean | null;
  ticketPriceCents?: number;
}): { paused: boolean; label: string } {
  const paused = eventAdmissionPausedLabel(input);
  if (paused.paused) return paused;
  if (typeof input.ticketPriceCents === "number") {
    return {
      paused: false,
      label: `Get Tickets · $${(input.ticketPriceCents / 100).toFixed(2)} + tax`,
    };
  }
  return { paused: false, label: "Get Tickets" };
}

export function resolveTicketRouteSlug(
  persistedSlug: string | null | undefined,
  routeSlug: string,
): string {
  return persistedSlug?.trim() || routeSlug;
}

export type PublicTicketStatus = {
  paymentStatus: "pending" | "paid" | "failed" | "refund_pending" | "refunded" | "disputed" | "cancelled";
  passStatus: "pending" | "issued" | "checked_in" | "revoked";
  holdExpiresAt: number | null;
};

export type TicketCheckoutDisposition =
  | "waiting"
  | "delayed"
  | "ready_for_pass"
  | "failed";

export function getTicketCheckoutDisposition(
  status: PublicTicketStatus | null,
  now: number,
): TicketCheckoutDisposition {
  if (!status) return "failed";
  if (
    status.paymentStatus === "failed" ||
    status.paymentStatus === "cancelled" ||
    status.paymentStatus === "refunded" ||
    status.paymentStatus === "disputed" ||
    status.passStatus === "revoked"
  ) {
    return "failed";
  }
  if (
    status.paymentStatus === "paid" &&
    (status.passStatus === "issued" || status.passStatus === "checked_in")
  ) {
    return "ready_for_pass";
  }
  if (
    status.paymentStatus === "pending" &&
    status.holdExpiresAt !== null &&
    status.holdExpiresAt <= now
  ) {
    return "delayed";
  }
  return "waiting";
}

export function shouldPollTicketCheckout(
  disposition: TicketCheckoutDisposition,
): boolean {
  return disposition === "waiting" || disposition === "delayed";
}

export function getTicketProcessingMessage(
  disposition: TicketCheckoutDisposition,
): string {
  if (disposition === "delayed") {
    return "Payment verification is taking longer than expected. Keep this page open while we wait for the payment provider.";
  }
  return "We’re waiting for the payment provider to confirm your purchase. Do not close this page.";
}

export function shouldRedirectTicketCheckoutToFailure({
  validPurchaseId,
  accessToken,
  disposition,
}: {
  validPurchaseId: boolean;
  accessToken: string | null | undefined;
  disposition: TicketCheckoutDisposition;
}): boolean {
  return !validPurchaseId || accessToken === null || disposition === "failed";
}

/** The server route already derives host scope; require its event parent too. */
export function publicPassMatchesEvent(passEventId: string, scopedEventId: string): boolean {
  return passEventId === scopedEventId;
}

export function isEventTicketPurchaseId(value: string): boolean {
  return /^[a-z0-9]{24,}$/i.test(value);
}

export function isNonRetryableTicketStatusError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("invalid argument") ||
    message.includes("validationerror") ||
    message.includes("not a valid id")
  );
}

const accessTokenPattern = /^evtaccess_[a-f0-9]{64}$/;

export const ticketCheckoutCapabilityKey = (purchaseId: string) =>
  `event-ticket-checkout:${purchaseId}`;

type CapabilityWindow = {
  location: { hash: string; pathname: string; search: string };
  sessionStorage: { getItem(key: string): string | null; setItem(key: string, value: string): void };
  history: { replaceState(data: unknown, title: string, url?: string | URL | null): void };
};

export function storeTicketCheckoutCapability(
  windowLike: Pick<CapabilityWindow, "sessionStorage">,
  purchaseId: string,
  accessToken: string,
): boolean {
  if (!accessTokenPattern.test(accessToken)) return false;
  try {
    windowLike.sessionStorage.setItem(ticketCheckoutCapabilityKey(purchaseId), accessToken);
    return true;
  } catch {
    return false;
  }
}

/**
 * Fragments never reach the server. Consume one immediately, then scrub the
 * URL and retain it only in same-tab sessionStorage for the settlement poll.
 */
export function bootstrapTicketCheckoutCapability(
  windowLike: CapabilityWindow,
  purchaseId: string,
): string | null {
  const fragment = new URLSearchParams(windowLike.location.hash.slice(1));
  const fragmentToken = fragment.get("ticket_token");
  if (windowLike.location.hash) {
    windowLike.history.replaceState(
      null,
      "",
      `${windowLike.location.pathname}${windowLike.location.search}`,
    );
  }
  if (fragmentToken && storeTicketCheckoutCapability(windowLike, purchaseId, fragmentToken)) {
    return fragmentToken;
  }
  try {
    const stored = windowLike.sessionStorage.getItem(ticketCheckoutCapabilityKey(purchaseId));
    return stored && accessTokenPattern.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

export type CanonicalTicketTotals = {
  quantity: number;
  unitPriceCents: number;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  currency: string;
};

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

import { splitRsvpFullName } from "@/lib/rsvp-name";

export type TicketBuyerDetails = {
  fullName: string;
  email: string;
  phone: string;
};

export type TicketBuyerErrors = Partial<Record<keyof TicketBuyerDetails, string>>;

export type TicketBuyerCheckoutPayload = {
  fullName: string;
  email: string;
  phone: string;
};

/** Split a single autofill-friendly name field for the checkout bridge. */
export function ticketBuyerCheckoutPayload(
  buyer: TicketBuyerDetails,
): TicketBuyerCheckoutPayload {
  return {
    fullName: buyer.fullName.trim(),
    email: buyer.email.trim(),
    phone: buyer.phone.trim(),
  };
}

/** Authoritative split used by the checkout bridge before Convex. */
export function splitTicketBuyerFullName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  return splitRsvpFullName(fullName);
}

export function clampTicketQuantity(quantity: number, maximum = 10): number {
  if (!Number.isFinite(quantity)) return 1;
  return Math.min(maximum, Math.max(1, Math.trunc(quantity)));
}

type TicketPaymentScrollTarget = {
  scrollIntoView(options?: ScrollIntoViewOptions): void;
};

/** Reset the viewport after the taller buyer form is replaced by payment. */
export function scrollTicketPaymentStepIntoView(
  target: TicketPaymentScrollTarget | null,
): void {
  target?.scrollIntoView({ block: "start" });
}

/** Keep first-step validation local. The checkout route remains authoritative. */
export function validateTicketBuyer(buyer: TicketBuyerDetails): TicketBuyerErrors {
  const errors: TicketBuyerErrors = {};
  const { firstName, lastName } = splitTicketBuyerFullName(buyer.fullName);
  if (!firstName.trim()) {
    errors.fullName = "Enter your name.";
  } else if (!lastName.trim()) {
    errors.fullName = "Enter your first and last name.";
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyer.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  const phoneDigits = buyer.phone.replace(/\D/g, "");
  if (phoneDigits.length > 0 && phoneDigits.length < 10) {
    errors.phone = "Enter a valid phone number.";
  }
  return errors;
}

/** Estimate only the line item before checkout; the server owns tax and final total. */
export function ticketPreCheckoutSummary({
  quantity,
  unitPriceCents,
  currency,
}: Pick<CanonicalTicketTotals, "quantity" | "unitPriceCents" | "currency">): {
  lineItemLabel: string;
  subtotalLabel: string;
  totalLabel: string;
} {
  const normalizedQuantity = clampTicketQuantity(quantity);
  const subtotalCents = normalizedQuantity * unitPriceCents;
  const subtotalLabel = formatMoney(subtotalCents, currency);
  return {
    lineItemLabel: `Admission passes (${normalizedQuantity} × ${formatMoney(unitPriceCents, currency)})`,
    subtotalLabel,
    totalLabel: `${subtotalLabel} + tax`,
  };
}

export function ticketPaymentTotalLabel(totals: CanonicalTicketTotals): string {
  return formatMoney(totals.totalCents, totals.currency);
}

/** Display exactly the server snapshots; this deliberately never computes tax. */
export function ticketTotalsRows(totals: CanonicalTicketTotals): [string, string][] {
  return [
    [
      `Tickets (${totals.quantity} × ${formatMoney(totals.unitPriceCents, totals.currency)})`,
      formatMoney(totals.subtotalCents, totals.currency),
    ],
    ["Tax", formatMoney(totals.taxCents, totals.currency)],
    ["Total", formatMoney(totals.totalCents, totals.currency)],
  ];
}
